import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Optional,
  Output,
  SkipSelf,
  ViewChild,
  forwardRef
} from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  ControlContainer,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors
} from '@angular/forms';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { BsModalService } from 'ngx-bootstrap/modal';
import { NGXLogger } from 'ngx-logger';
import { ReplaySubject, forkJoin, fromEventPattern } from 'rxjs';
import { filter, map, shareReplay, take } from 'rxjs/operators';

import { environment as env } from '../../../../environments/environment';
import { FileModuleEnum } from '../../../shared/enum/file-url.enum';
import { MonthlyPartnerProfitSharingUploadStatusEnum } from '../../../shared/enum/monthly-partner-profit-sharing.enum';
import { AlertModalComponent } from '../../../shared/layouts';
import { MonthlyPartnerProfitSharingService } from '../../../shared/services/monthly-partner-profit-sharing.service';

export class ICustomFile extends File {
  errors?: { [key: string]: any };
  imgSrc?: string;
  filePath?: string | ArrayBuffer | null;
  id?: number;
  imgHeight?: number;
  imgWidth?: number;
  isImg?: boolean;
  imgLoadReplay?: ReplaySubject<[Event, ProgressEvent]>;
  textContent?: string;
  textLoadReplay?: ReplaySubject<ProgressEvent>;
}
type allowedType = RegExp | string | string[];

@Component({
  selector: 'app-monthly-partner-profit-sharing-file-upload',
  templateUrl: './monthly-partner-profit-sharing-file-upload.component.html',
  styleUrls: ['./monthly-partner-profit-sharing-file-upload.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MonthlyPartnerProfitSharingFileUploadComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => MonthlyPartnerProfitSharingFileUploadComponent),
      multi: true
    }
  ]
})
export class MonthlyPartnerProfitSharingFileUploadComponent
  extends OnDestroyMixin
  implements OnChanges, OnInit, ControlValueAccessor
{
  @HostBinding('class.is-invalid') get invalid() {
    return this.hasError;
  }

  constructor(
    @Optional() @SkipSelf() private readonly controlContainer: ControlContainer,
    protected readonly modalService: BsModalService,
    private readonly monthlyPartnerProfitSharingService: MonthlyPartnerProfitSharingService,
    private readonly logger: NGXLogger
  ) {
    super();
    this.disabled = false;
    this.hasError = false;
    this.hasChange = false;
    this.ngChange = () => {};
    this.ngTouched = () => {};
  }

  @Input()
  set allowedExt(value: allowedType) {
    if (typeof value === 'string') {
      value = value + '$';
    }
    if (value instanceof Array) {
      value = value.join('|') + '$';
    }
    this._allowedExt = value;
  }

  get allowedExt(): allowedType {
    return this._allowedExt;
  }

  private _allowedExt: allowedType;
  url = '';
  inputValue;
  isLoad = true;
  ngChange;
  ngTouched;
  value;
  validator: AsyncValidatorFn;
  fileList: ICustomFile[] = [];
  progress;
  fileService;

  @ViewChild('uploadInput') uploadInput: ElementRef;

  @Input() disabled: boolean;
  @Input() multiple: boolean;
  @Input() allowedTypes: allowedType;
  @Input() size: number;
  @Input() hasError: boolean;
  @Input() hasChange: boolean;
  @Input() withMeta: boolean;
  @Input() maxHeight: number;
  @Input() maxWidth: number;
  @Input() controlName: string;
  @Input() initialFileList: ICustomFile[];
  @Input() isNew: boolean;
  @Input() fileTypeErrorTxt: string;
  @Input() fileSizeErrorTxt: string;
  @Input() descriptionTxt: string | null;
  @Input() isAddable: boolean;
  @Input() index: number;
  @Input() maxImages: number;
  @Input() lastIndex: number;
  @Input() fileModule: FileModuleEnum;
  @Input() runDate: string;

  @Output() addImage = new EventEmitter<string>();

  @HostListener('change', ['$event.target.files']) onChange = (_value: any) => {};
  @HostListener('blur') onTouched = () => {};

  ngOnInit(): void {
    this.fileList = this.initialFileList || null;
  }

  ngOnChanges(): void {
    this.ngChange(this.value);
  }

  propagateChange: any = () => {};

  writeValue(fileList): void {
    if (fileList && fileList.length) {
      fileList
        .filter(file => !(file.filePath instanceof ArrayBuffer))
        .forEach(file => {
          this.fileService(file.filePath as string)
            .pipe(filter(data => Boolean(data)))
            .subscribe(signedUrlObject => (file.filePath = signedUrlObject.signedUrl));
        });
    }

    this.value = this.fileList = this.inputValue = fileList;
  }

  registerOnChange(fn: any): void {
    this.onChange = this.onChangeGenerator(fn);
  }

  registerOnTouched(fn: any): void {
    this.ngTouched = fn;
    this.propagateChange = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private onChangeGenerator(fn: (_: any) => {}): (_: ICustomFile[]) => void {
    if (this.value) {
      this.value.forEach(imgObj => {
        const regexpImageType = new RegExp(env.regexp.imageType, 'ig');
        imgObj.isImg = regexpImageType.test(imgObj.name);
      });
    }
    this.ngChange(this.value);
    this.ngTouched();

    return (customFiles: ICustomFile[]) => {
      const fileArr: File[] = [];
      this.isLoad = false;

      for (const file of customFiles) {
        if (this.withMeta && FileReader) {
          const fileReader = new FileReader();
          this.generateFileMeta(file, fileReader);
        }
        file.errors = {};
        fileArr.push(file);
      }

      fn(fileArr);
    };
  }

  private generateFileMeta(customFile: ICustomFile, fileReader: FileReader) {
    if (customFile.type.match(/text.*/)) {
      customFile.textLoadReplay = this.setText(customFile, fileReader);
    } else if (customFile.type.match(/image.*/)) {
      customFile.imgLoadReplay = this.setImage(customFile, fileReader);
    }
  }

  private setText(customFile: ICustomFile, fileReader: FileReader): ReplaySubject<ProgressEvent> {
    const onloadReplay = new ReplaySubject<ProgressEvent>(1);
    const frLoadObs = fromEventPattern<ProgressEvent>(
      (handler: any) => fileReader.addEventListener('load', handler),
      (handler: any) => fileReader.removeEventListener('load', handler)
    ).pipe(take(1), shareReplay());

    frLoadObs.subscribe(onloadReplay);
    frLoadObs.pipe(take(1)).subscribe(() => {
      customFile.textContent = fileReader.result + '';
    });

    fileReader.readAsText(customFile);

    return onloadReplay;
  }

  private setImage(customFile: ICustomFile, fileReader: FileReader): ReplaySubject<[Event, ProgressEvent]> {
    customFile.isImg = true;

    const img = new Image();

    const imgLoadObs = fromEventPattern<Event>(
      (handler: any) => img.addEventListener('load', handler),
      (handler: any) => img.removeEventListener('load', handler)
    ).pipe(take(1), shareReplay());

    const frLoadObs = fromEventPattern<ProgressEvent>(
      (handler: any) => fileReader.addEventListener('load', handler),
      (handler: any) => fileReader.removeEventListener('load', handler)
    ).pipe(take(1), shareReplay());

    const onloadReplay = new ReplaySubject<[Event, ProgressEvent]>(1);
    const observables = [imgLoadObs, frLoadObs];

    forkJoin(observables).pipe(take(1)).subscribe(onloadReplay);

    imgLoadObs.pipe(take(1)).subscribe(() => {
      customFile.imgHeight = img.height;
      customFile.imgWidth = img.width;
    });

    frLoadObs.pipe(take(1)).subscribe(() => {});

    fileReader.readAsDataURL(customFile);

    return onloadReplay;
  }

  private generateRegExp(pattern: allowedType): RegExp | null {
    if (!pattern) {
      return null;
    }

    if (pattern instanceof RegExp) {
      return new RegExp(pattern);
    } else if (typeof pattern === 'string') {
      return new RegExp(pattern, 'ig');
    } else if (pattern instanceof Array) {
      return new RegExp(`(${pattern.join('|')})`, 'ig');
    }

    return null;
  }

  validate(files: AbstractControl) {
    if (!files.value || !files.value.length || files.disabled) {
      return null;
    }

    const errors: ValidationErrors = {};
    const loaders: ReplaySubject<ProgressEvent>[] = [];

    for (const file of files.value) {
      if (this.size && this.size < file.size) {
        file.errors['fileSize'] = true;
        errors['fileSize'] = true;
      }

      if (file.isImg && (this.maxWidth || this.maxHeight)) {
        loaders.push(
          file.imgLoadReplay.pipe(
            take(1),
            map((event: ProgressEvent) => {
              if (this.maxWidth && file.imgWidth > this.maxWidth) {
                file.errors['imageWidth'] = true;
                errors['imageWidth'] = true;
              }
              if (this.maxHeight && file.imgHeight > this.maxHeight) {
                file.errors['imageHeight'] = true;
                errors['imageHeight'] = true;
              }
              return event;
            })
          )
        );
      }

      if (!this.allowedExt && !this.allowedTypes) {
        continue;
      }

      const extP = this.generateRegExp(this.allowedExt);
      const typeP = this.generateRegExp(this.allowedTypes);

      if (extP && !extP.test(file.name)) {
        file.errors['fileExt'] = true;
        errors['fileExt'] = true;
      }

      if (typeP && file.type && !typeP.test(file.type)) {
        file.errors['fileType'] = true;
        errors['fileType'] = true;
      }
    }

    if (this.isLoad) {
      return;
    }

    this.fileList = files.value;

    if (Object.keys(errors).length) {
      return errors;
    }

    this.monthlyPartnerProfitSharingService
      .uploadMonthlyPartnerProfitSharingFiles(files.value, this.runDate)
      .forEach((allRes, index: number) =>
        allRes
          .pipe(
            filter(Boolean),
            filter(res => res['status'])
          )
          .subscribe({
            next: res => {
              if (!this.fileList[index].filePath) {
                const reader = new FileReader();

                reader.readAsDataURL(files.value[index]);
                reader.onload = () => (this.fileList[index].filePath = reader.result);
              }

              if (res['status'] === 'progress') {
                return JSON.stringify(res);
              } else if (res['isValidImport'] && res['validations'] && !res['validations'].length) {
                files.value[index].status = MonthlyPartnerProfitSharingUploadStatusEnum.COMPLETED;
                files.markAsTouched();
              } else if (res['validations'] && res['validations'].length) {
                files.value[index].errorMessage = `${res['validations'].length} Error Found.`;
                files.value[index].validations = res['validations'];
                files.value[index].status = MonthlyPartnerProfitSharingUploadStatusEnum.FAILED;
              } else {
                this.logger.info(`FileIndex(${index})-Unhandled`, res);
                files.value[index].errorMessage = res['error'] && res['error'].message;
                files.value[index].status = MonthlyPartnerProfitSharingUploadStatusEnum.FAILED;
              }
            },
            error: err => this.logger.error(`FileIndex(${index})-Error`, err)
          })
      );
  }

  onClickDelete() {
    this.fileList = [];
    this.controlContainer.control.get(this.controlName).setValue([]);
  }

  alertFailModal(isFileTypeError, isFileSizeError) {
    const message = isFileTypeError
      ? this.fileTypeErrorTxt
      : `File size limit exceeded. <br/><br/> ${this.fileSizeErrorTxt}`;
    const initialState = {
      title: 'Failed',
      message
    };
    const confirmModalRef = this.modalService.show(AlertModalComponent, {
      initialState
    });

    this.logger.debug('alertFailModal->isFileSizeError', isFileSizeError);
    confirmModalRef.content.action.pipe(untilComponentDestroyed(this)).subscribe(() => {
      this.fileList = [];
      this.controlContainer.control.get(this.controlName).setValue([]);
    });
  }
}
